/**
 * Bookmarks screen (route: Bookmarks).
 *
 * BARE-RN port (P3-2): expo-router Stack.Screen/useRouter → react-navigation
 * (header set in the navigator; useNavigation for routing). @expo/vector-icons
 * → local Icon shim. Behaviour unchanged.
 */
import { memo, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '../components/ui/Icon';
import { colors, spacing, radius, fonts, getLayout, typographyScale } from '../constants/theme';
import { api } from '../services/api';
import PostCard, { Post } from '../components/feed/PostCard';
import ParticleBackground from '../components/ui/ParticleBackground';
import { GlassBar } from '../components/glass';
import Asset3D from '../components/ui/Asset3D';
import ReportBlockSheet, { ReportTarget } from '../components/moderation/ReportBlockSheet';
import { isBlocked, onBlockedChange, isMuted, onMutedChange } from '../services/moderation';
import { applyLike, applyBookmark } from '../services/postEngagement';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList>;

// Raw bookmark item (camelCase Prisma envelope, same as home/profile feed):
// { id, content/text, createdAt, repostCount, mediaUrls, User:{displayName,
//   avatar,username}, _count:{Vote,Comment} } with flat compat fields
// (likes/comments/text). Mapped onto the client shape via toPostCardFormat().
interface BookmarkPost {
  id: string;
  content?: string;
  text?: string;
  createdAt?: string;
  created_at?: string;
  repostCount?: number;
  mediaUrls?: string[];
  image_url?: string;
  likes?: number;
  comments?: number;
  User?: {
    displayName?: string;
    username?: string;
    avatar?: string;
  };
  _count?: {
    Vote?: number;
    Comment?: number;
  };
  // Local optimistic like state (server sends `liked` on the bookmark row; we
  // also mutate these in place so the PostCard reflects a like/unlike toggle).
  liked?: boolean;
}

function timeAgo(dateStr?: string | null): string {
  if (!dateStr) return '';
  const ts = new Date(dateStr).getTime();
  if (isNaN(ts)) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

// Normalize like home/profile: author from bp.User; counts from bp._count.Vote
// (likes) / bp._count.Comment (comments) / bp.repostCount; with flat
// likes/comments/text compat fallbacks the backend also sends.
function toPostCardFormat(bp: BookmarkPost): Post {
  const u = bp.User || {};
  return {
    id: bp.id,
    user: {
      name: u.displayName || u.username || 'User',
      handle: u.username || '',
      avatar: u.avatar,
    },
    content: bp.content || bp.text || '',
    image: bp.mediaUrls?.[0] || bp.image_url,
    time: timeAgo(bp.createdAt || bp.created_at),
    likes: bp._count?.Vote ?? bp.likes ?? 0,
    comments: bp._count?.Comment ?? bp.comments ?? 0,
    reposts: bp.repostCount ?? 0,
    liked: bp.liked ?? false,
    bookmarked: true,
  };
}

// Memoized feed row. Binds the parent's STABLE per-list handlers against this
// row's item via useCallback so PostCard's React.memo actually holds — a like on
// one card no longer re-renders every other bookmark row.
const BookmarkRow = memo(function BookmarkRow({
  item,
  onOpen,
  onLike,
  onUnbookmark,
  onOptions,
}: {
  item: BookmarkPost;
  onOpen: (id: string) => void;
  onLike: (post: BookmarkPost) => void;
  onUnbookmark: (post: BookmarkPost) => void;
  onOptions: (post: BookmarkPost) => void;
}) {
  const handlePress = useCallback(() => onOpen(item.id), [onOpen, item.id]);
  const handleLike = useCallback(() => onLike(item), [onLike, item]);
  const handleUnbookmark = useCallback(() => onUnbookmark(item), [onUnbookmark, item]);
  const handleOptions = useCallback(() => onOptions(item), [onOptions, item]);
  return (
    <View style={styles.cardWrap}>
      <PostCard
        post={toPostCardFormat(item)}
        onPress={handlePress}
        onLike={handleLike}
        onComment={handlePress}
        onBookmark={handleUnbookmark}
        onOptions={handleOptions}
      />
    </View>
  );
});

export default function BookmarksScreen() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  // Self-manage the header (skill §12): hide the opaque native stack bar and
  // render an in-screen translucent GlassBar so the aurora + content scroll
  // UNDER it edge-to-edge — no fixed opaque strip, no gap under the notch.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);
  // Reserved top inset so the first card clears the floating glass header
  // (title row ≈ 44 + bottom padding) plus the safe-area notch.
  const headerHeight = insets.top + spacing.sm + 44;
  const [bookmarks, setBookmarks] = useState<BookmarkPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  // Trust & Safety (Apple 1.2): report/block sheet target + re-render on block.
  const [tsTarget, setTsTarget] = useState<ReportTarget | null>(null);
  const [, setBlockTick] = useState(0);
  useEffect(() => {
    const bump = () => setBlockTick((n) => n + 1);
    const unsubBlocked = onBlockedChange(bump);
    const unsubMuted = onMutedChange(bump);
    return () => { unsubBlocked(); unsubMuted(); };
  }, []);

  const fetchBookmarks = useCallback(async () => {
    setError(false);
    try {
      // Backend wraps as { data: { bookmarks: [...], total } }; api() unwraps
      // one level, so the array is at res.data.bookmarks.
      const res = await api<{ bookmarks: BookmarkPost[] }>('/posts/bookmarks');
      if (res.success && res.data) {
        setBookmarks(Array.isArray(res.data.bookmarks) ? res.data.bookmarks : []);
      } else {
        setBookmarks([]);
        if (!res.success) setError(true);
      }
    } catch {
      setBookmarks([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchBookmarks();
    setRefreshing(false);
  }, [fetchBookmarks]);

  useEffect(() => {
    fetchBookmarks();
  }, [fetchBookmarks]);

  // Optimistic like toggle — mirror the home/detail pattern: read the prior liked
  // state, POST to like / DELETE to unlike, update the local row immediately (so
  // the card re-renders) and route through the shared engagement store so the feed
  // + detail stay in sync; roll back both on failure.
  // useCallback (stable identity) so the memoized BookmarkRow below isn't
  // re-rendered on every parent state change.
  const handleLike = useCallback(async (post: BookmarkPost) => {
    const wasLiked = post.liked ?? false;
    const rollbackStore = applyLike(post.id, !wasLiked);
    setBookmarks((prev) =>
      prev.map((b) =>
        b.id === post.id
          ? {
              ...b,
              liked: !wasLiked,
              _count: { ...b._count, Vote: Math.max(0, (b._count?.Vote ?? b.likes ?? 0) + (wasLiked ? -1 : 1)) },
            }
          : b
      )
    );
    const res = await api(`/posts/${post.id}/like`, { method: wasLiked ? 'DELETE' : 'POST' });
    if (!res.success) {
      rollbackStore();
      setBookmarks((prev) =>
        prev.map((b) =>
          b.id === post.id
            ? {
                ...b,
                liked: wasLiked,
                _count: { ...b._count, Vote: Math.max(0, (b._count?.Vote ?? b.likes ?? 0) + (wasLiked ? 1 : -1)) },
              }
            : b
        )
      );
    }
  }, []);

  // Un-bookmark from this screen: DELETE the bookmark and remove the row
  // optimistically (restore it on failure). Also updates the shared store so the
  // bookmark flag stays in sync on the feed/detail card. Rollback captures the
  // removed row via functional setState so this stays a stable useCallback (no
  // `bookmarks` closure dependency).
  const handleUnbookmark = useCallback(async (post: BookmarkPost) => {
    const rollbackStore = applyBookmark(post.id, false);
    let removed: BookmarkPost | undefined;
    let removedIndex = -1;
    setBookmarks((prev) => {
      removedIndex = prev.findIndex((b) => b.id === post.id);
      removed = removedIndex >= 0 ? prev[removedIndex] : undefined;
      return prev.filter((b) => b.id !== post.id);
    });
    const res = await api(`/posts/${post.id}/bookmark`, { method: 'DELETE' });
    if (!res.success) {
      rollbackStore();
      // Restore the removed row at its original position.
      if (removed) {
        setBookmarks((prev) => {
          if (prev.some((b) => b.id === post.id)) return prev;
          const next = prev.slice();
          const at = removedIndex >= 0 && removedIndex <= next.length ? removedIndex : next.length;
          next.splice(at, 0, removed!);
          return next;
        });
      }
    }
  }, []);

  // Stable navigation + options handlers (item passed through) so the memoized
  // row keeps its props identity across unrelated re-renders.
  const openPost = useCallback((id: string) => {
    navigation.navigate('Post', { id });
  }, [navigation]);
  const openOptions = useCallback((post: BookmarkPost) => {
    setTsTarget({
      type: 'post',
      contentId: post.id,
      authorUsername: post.User?.username,
      authorLabel: `@${post.User?.username || 'user'}`,
    });
  }, []);

  return (
    <View style={styles.container}>
      {/* Full-bleed aurora under the status bar (skill §12/16) — sole surface. */}
      <ParticleBackground />

      {/* Floating translucent header (skill §12): back + title, edge-to-edge,
          content scrolls underneath. The bright top sheen (GlassBar default)
          reads as light catching the material. */}
      <GlassBar
        radius={0}
        style={styles.headerBar}
        contentStyle={[styles.header, { paddingTop: insets.top }]}
      >
        <View style={[styles.headerInner, containerStyle]}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.headerBackBtn}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Ionicons name="chevron-back" size={24} color={colors.ink} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Bookmarks</Text>
          {/* Symmetry spacer so the title sits optically centered against the
              back chevron (no right-side action on this screen). */}
          <View style={styles.headerBackBtn} />
        </View>
      </GlassBar>

      {loading ? (
        <View style={[styles.loadingWrap, { paddingTop: headerHeight }]}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={(Array.isArray(bookmarks) ? bookmarks : []).filter((b) => !(isBlocked(b.User?.username) || isMuted(b.User?.username)))}
          keyExtractor={(item, index) => item.id || String(index)}
          renderItem={({ item }) => (
            <BookmarkRow
              item={item}
              onOpen={openPost}
              onLike={handleLike}
              onUnbookmark={handleUnbookmark}
              onOptions={openOptions}
            />
          )}
          ListEmptyComponent={
            error ? (
              <View style={styles.emptyWrap}>
                <Asset3D name="error" size={120} style={{ marginBottom: spacing.sm }} />
                <Text style={styles.emptyText}>Couldn't load bookmarks</Text>
                <Text style={styles.emptySubtext}>
                  Check your connection and try again.
                </Text>
                <TouchableOpacity
                  style={styles.retryBtn}
                  onPress={onRefresh}
                  activeOpacity={0.8}
                  accessibilityRole="button"
                  accessibilityLabel="Retry loading bookmarks"
                >
                  <Ionicons name="refresh-outline" size={16} color="#FFFFFF" />
                  <Text style={styles.retryText}>Retry</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.emptyWrap}>
                <Asset3D name="gem" size={120} style={{ marginBottom: spacing.sm }} />
                <Text style={styles.emptyText}>No bookmarks yet</Text>
                <Text style={styles.emptySubtext}>
                  Save posts to find them here later
                </Text>
              </View>
            )
          }
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
            />
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[
            styles.listContent,
            // Top inset so the first card clears the floating glass header —
            // the header reserves no layout space; content scrolls under it.
            { paddingTop: headerHeight + spacing.xs },
            containerStyle,
          ]}
        />
      )}
      <ReportBlockSheet
        visible={!!tsTarget}
        target={tsTarget}
        onClose={() => setTsTarget(null)}
        onBlocked={() => setTsTarget(null)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    // No opaque fill: ParticleBackground (absoluteFill) is the sole surface so
    // the aurora bleeds full-screen under the notch (skill §12/16).
    flex: 1,
  },
  // Floating glass header shell: pinned to the top edge, edge-to-edge, list
  // scrolls underneath. zIndex keeps it above the FlatList.
  headerBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  header: {
    paddingHorizontal: spacing.xs,
    paddingBottom: spacing.sm,
  },
  headerInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  // ≥44pt tap target (iOS HIG); doubles as the right-side symmetry spacer.
  headerBackBtn: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    // Hierarchy from weight + tightened tracking (skill §15) — vibrant over the
    // translucent bar rather than flat gray.
    ...typographyScale.h3,
    ...fonts.bold,
    color: colors.ink,
    flex: 1,
    textAlign: 'center',
  },
  cardWrap: {
    marginHorizontal: spacing.lg,
  },
  listContent: {
    paddingBottom: spacing.xxxl,
    flexGrow: 1,
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 80,
    gap: spacing.sm,
  },
  emptyText: {
    fontSize: 16,
    color: colors.ink,
    ...fonts.semibold,
  },
  emptySubtext: {
    fontSize: 13,
    color: colors.muted,
    textAlign: 'center',
    ...fonts.regular,
  },
  retryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm + 2,
    borderRadius: radius.full,
    marginTop: spacing.md,
    gap: spacing.xs,
    minHeight: 44,
  },
  retryText: {
    fontSize: 14,
    color: '#FFFFFF',
    ...fonts.semibold,
  },
});
